import { Check, ChevronsUpDown, X } from 'lucide-react'
import * as React from 'react'
import { Fragment } from 'react'

import { Button } from '~/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command'
import { Label } from '~/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import { cn } from '~/lib/utils'

interface ComboboxProps<T> {
  options?: T[] // For flat list
  groupedOptions?: { group: string; options: T[] }[] // For grouped list
  value?: T
  onChange: (value: T | undefined) => void
  placeholder?: string
  displayValue: (option: T) => string
  disabled?: boolean
  className?: string
  loading?: boolean
  emptyMessage?: string
  itemKey?: (option: T) => string | number | undefined
  name?: string
  error?: boolean
  helperText?: React.ReactNode
  label?: string
  searchPlaceholder?: string
  onSearchChange?: (search: string) => void
  shouldFilter?: boolean
  renderOption?: (option: T) => React.ReactNode
  filterValue?: (option: T) => string
  popoverClassName?: string
  clearable?: boolean
  modal?: boolean
}

export function Combobox<T>({
  options,
  groupedOptions,
  value,
  onChange,
  placeholder = 'Select item',
  displayValue,
  disabled,
  className,
  loading,
  emptyMessage = 'No item found.',
  itemKey = (option: T) => displayValue(option),
  name,
  error,
  helperText,
  label,
  searchPlaceholder = 'Search...',
  onSearchChange,
  shouldFilter,
  renderOption,
  filterValue,
  popoverClassName,
  clearable,
  modal,
}: ComboboxProps<T>) {
  const id = React.useId()
  const [open, setOpen] = React.useState<boolean>(false)

  const handleSelect = (selectedValue: T) => {
    onChange(selectedValue)
    setOpen(false)
  }

  const isSelected = (option: T) => {
    if (!value) return false
    const valueKey = itemKey(value)
    const optionKey = itemKey(option)
    return valueKey !== undefined && optionKey !== undefined && valueKey === optionKey
  }

  const hasOptions = groupedOptions
    ? groupedOptions.some((group) => group.options.length > 0)
    : options && options.length > 0

  return (
    <div className={cn('w-full space-y-2', className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <div className='relative flex items-center'>
          <PopoverTrigger asChild>
            <Button
              id={id}
              variant='outline'
              role='combobox'
              aria-expanded={open}
              disabled={disabled || loading}
              className={cn(
                'bg-transparent hover:bg-transparent cursor-text border-input w-full justify-between px-3 py-2 font-normal outline-offset-0 outline-none focus-visible:outline-[3px] h-12',
                clearable && value && 'pr-8',
                error && 'border-destructive',
              )}
            >
              {value ? (
                <span className='flex min-w-0 items-center gap-2'>
                  <span className='truncate'>{displayValue(value)}</span>
                </span>
              ) : (
                <span className='text-muted-foreground'>{placeholder}</span>
              )}
              {!(clearable && value) && (
                <ChevronsUpDown className='text-muted-foreground/80 shrink-0' aria-hidden='true' />
              )}
            </Button>
          </PopoverTrigger>
          {clearable && value && (
            <button
              type='button'
              aria-label='Clear selection'
              className='absolute right-2 p-0.5 rounded-sm text-muted-foreground/80 hover:text-foreground'
              onClick={(e) => {
                e.preventDefault()
                onChange(undefined)
              }}
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </div>
        <PopoverContent
          className={cn(
            'border-input w-full min-w-[var(--radix-popper-anchor-width)] p-0',
            popoverClassName,
          )}
          align='start'
        >
          <Command shouldFilter={shouldFilter !== undefined ? shouldFilter : !onSearchChange}>
            <CommandInput placeholder={searchPlaceholder} onValueChange={onSearchChange} />
            <CommandList>
              {loading && <div className='p-2 text-center text-sm'>Loading...</div>}
              {!loading && !hasOptions && <CommandEmpty>{emptyMessage}</CommandEmpty>}
              {!loading && hasOptions && (
                <>
                  {groupedOptions
                    ? groupedOptions.map((group) => (
                        <Fragment key={group.group}>
                          <CommandGroup heading={group.group}>
                            {group.options.map((item) => (
                              <CommandItem
                                key={String(itemKey(item) ?? displayValue(item))}
                                value={displayValue(item)}
                                onSelect={() => handleSelect(item)}
                              >
                                {displayValue(item)}
                                {isSelected(item) && <Check size={16} className='ml-auto' />}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Fragment>
                      ))
                    : options?.map((item) => (
                        <CommandItem
                          key={String(itemKey(item) ?? displayValue(item))}
                          value={filterValue ? filterValue(item) : displayValue(item)}
                          onSelect={() => handleSelect(item)}
                        >
                          {renderOption ? renderOption(item) : displayValue(item)}
                          {isSelected(item) && <Check size={16} className='ml-auto' />}
                        </CommandItem>
                      ))}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && helperText && <p className='text-sm font-medium text-destructive'>{helperText}</p>}
      {/* Hidden input for form compatibility */}
      {name && (
        <input type='hidden' name={name} value={value ? String(itemKey(value) ?? '') : ''} />
      )}
    </div>
  )
}
